import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, ReplaySubject, Subject, forkJoin, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  CampaignDetails,
  CampaignInputs,
  CampaignRecipientStatuses,
  CampaignStatus,
  CampaignType,
  convertToEnum,
  EventDetails,
  HighMessagingDetails,
  Notification,
  RewardDetails
}
  from '../../models/engage/campaign-details.model';
import { environment } from '../../../environments/environment';
import { z } from 'zod';
import { CampaignSource } from './ICampaignSource';
import moment from 'moment-timezone';
import { EngageConstants } from '../engage.constants';

interface HeroImageRequest {
  desktopURL: string;
  mobileURL: string;
}

interface EmailCustomizationsRequest {
  subject: string;
  body: string;
  themeID: string;
  heroImage?: HeroImageRequest;
  recruitmentUrl?: string | null;
}

interface NotificationRequest {
  layoutID: string;
  sendDatetime: string;
  emailCustomizations: EmailCustomizationsRequest;
  segment: string;
  channel: string;
}

interface EventRequest {
  startDatetime: string;
  endDatetime: string;
  venue: string;
  rsvpRequired: boolean;
  adultsOnly: boolean;
  maxReservations?: number;
  maxAttendeesPerCustomer?: number;
  subject: string;
  body: string;
}

interface RewardRequest {
  startDatetime: string;
  endDatetime: string;
  appMessage: string;
  includeOperatorImage: boolean;
  includeActiveLocationRewardCustomers: boolean;
  rewardId: number | string;
}

interface BaseCampaignRequest {
  audienceID: string;
  name: string;
  type: string;
  title: string;
  templateID: string;
  goal?: string;
}

interface CampaignRequest extends BaseCampaignRequest {
  notifications: NotificationRequest[];
  event?: EventRequest;
  reward?: RewardRequest;
}

export class DigitalMarketingCampaignSource implements CampaignSource {
  private readonly DEFAULT_SEGMENT = 'CAMPAIGN';
  private readonly NOT_RSVPED_SEGMENT = 'NOT_RSVPED';
  private readonly RSVPED_YES_SEGMENT = 'RSVPED_YES';
  private readonly RSVPED_YES_OR_PENDING_SEGMENT = 'RSVPED_YES_OR_PENDING';
  private readonly DEFAULT_CHANNEL = 'EMAIL';
  private previousCampaign;
  constructor(
    private readonly httpClient: HttpClient
  ) { }

  public getCampaignDetails(locationNumber: string, campaignID: string): Observable<CampaignDetails> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const campaignDetails: Subject<CampaignDetails> = new Subject<CampaignDetails>();
    this.httpClient.get<CampaignDetails>(environment.digitalMarketingCampaignsServiceURL + `/api/restaurant/campaigns/${campaignID}`,
      { headers: headers }).subscribe({
      next: (response) => {
        this.previousCampaign = response;
        campaignDetails.next(this.campaignDetailsResponseToInternalType(campaignDetailsSchema.parse(response)))
        campaignDetails.complete()
      },
      error: (error) => {
        campaignDetails.error(error.error);
      }
    }
    )
    return campaignDetails.asObservable();
  }

  public getLocationHighMessagingAlertDetails(locationNumber: string): Observable<boolean> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const alert: Subject<boolean> = new Subject<boolean>();
    this.httpClient.get<HighMessagingDetails>(
      environment.digitalMarketingCampaignsServiceURL + `/api/campaigns/alerts?emailCountThreshold=10&numDays=84`,
      { headers: headers }
    ).subscribe({
      next: (response) => {
        alert.next(this.interpretHighMessagingResponse(highMessagingResponseSchema.parse(response)));
        alert.complete()
      },
      error: (error) => {
        alert.next(false);
        alert.complete()
      }
    }
    )
    return alert.asObservable();
  }

  public getCampaignRecipientStatuses(locationNumber: string, path: string, campaignID: string | undefined = undefined):
      Observable<CampaignRecipientStatuses> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const campaignRecipients: Subject<CampaignRecipientStatuses> = new Subject<CampaignRecipientStatuses>();
    this.httpClient.get(environment.digitalMarketingCampaignsServiceURL + path, { headers: headers }).subscribe({
      next: (response) => {
        campaignRecipients.next(this.recipientsResponseToInternalType(campaignRecipientsSchema.parse(response), campaignID))
        campaignRecipients.complete()
      },
      error: (error) => {
        campaignRecipients.error(error.error)
      }
    }
    );
    return campaignRecipients.asObservable();
  }

  public createCampaign(locationNumber: string, campaignInputs: CampaignInputs): Observable<string> {
    if (this.isScheduledSendTimeElapsed(campaignInputs)) {
      return throwError(() => EngageConstants.scheduledTimeElapsedError);
    }

    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });

    let campaignRequest = this.formatCampaignRequest(campaignInputs);
    // Only set explicit recipients when there is no audienceID.
    // For audience-based campaigns, recipients are determined by the audience and
    // should not be sent as a separate recipients list.
    if (campaignInputs.cfaIDs && !campaignRequest.audienceID) {
      campaignRequest['recipients'] = campaignInputs.cfaIDs;
    }

    const campaignID: Subject<string> = new Subject<string>();
    this.httpClient.post(environment.digitalMarketingCampaignsServiceURL + '/api/restaurant/campaigns',
      campaignRequest, { headers: headers })
      .subscribe({
        next: (response) => {
          campaignID.next(createCampaignResponseSchema.parse(response).id);
          campaignID.complete();
        },
        error: (error) => {
          campaignID.error(error.error);
        }
      });
    return campaignID.asObservable();
  }

  public updateCampaign(locationNumber: string, campaignInputs: CampaignInputs): Observable<string> {
    if (this.isScheduledSendTimeElapsed(campaignInputs)) {
      return throwError(() => EngageConstants.scheduledTimeElapsedError);
    }

    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    let campaignRequest = this.formatCampaignRequest(campaignInputs);
    const campaignID: ReplaySubject<string> = new ReplaySubject<string>(1);

    const prevNotifications = (this.previousCampaign?.notifications || []).map(n => ({
      ...n,
      sendDatetime: n.sendDatetime ? moment(n.sendDatetime).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : n.sendDatetime
    }));
    const currNotifications = campaignRequest.notifications || [];
    const findByLayoutID = (arr, layoutID) => arr.find(n => n.layoutID === layoutID);

    // Compute nameChanged and eventChanged before addOps
    const nameChanged = campaignRequest.name !== this.previousCampaign?.name;
    let prevEvent = this.previousCampaign?.event;
    let normalizedPrevEvent = prevEvent ? {
      ...prevEvent,
      startDatetime: prevEvent.startDatetime ?
        moment(prevEvent.startDatetime).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : prevEvent.startDatetime,
      endDatetime: prevEvent.endDatetime ?
        moment(prevEvent.endDatetime).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : prevEvent.endDatetime
    } : undefined;
    const eventChanged = !deepEqual(normalizedPrevEvent, campaignRequest.event);
    const omitEventSubject = (event) => {
      if (!event) {
        return event;
      }
      const { subject, ...rest } = event;
      return rest;
    };
    const significantEventChanged = !deepEqual(
      omitEventSubject(normalizedPrevEvent),
      omitEventSubject(campaignRequest.event)
    );

    // Add new notifications
    const addOps = currNotifications
      .filter(n => {
        const prev = findByLayoutID(prevNotifications, n.layoutID);
        if (n.layoutID === 'event-update') {
          return significantEventChanged;
        }
        return !prev || prev?.creationStatus === CampaignStatus.Cancelled;
      })
      .map(n => this.addNotificationToExistingCampaign(locationNumber, n, campaignInputs.campaignID).pipe(
        catchError(err => of({ error: err }))
      ));

    // Update changed notifications
    const updateOps = currNotifications
      .map(n => {
        const prev = findByLayoutID(prevNotifications, n.layoutID);
        if (prev) {
          const { id, creationStatus, ...prevRest } = prev;
          if (prevRest.emailCustomizations && typeof prevRest.emailCustomizations.recruitmentUrl === 'undefined') {
            prevRest.emailCustomizations = {
              ...prevRest.emailCustomizations,
              recruitmentUrl: null
            };
          }
          if (!deepEqual(prevRest, n)
              && n.layoutID !== 'event-update'
              && creationStatus !== CampaignStatus.Cancelled
              && creationStatus !== CampaignStatus.Completed) {
            const updatedNotification = {
              ...prev,
              ...n
            };
            return this.updateNotificationForExistingCampaign(locationNumber, updatedNotification, campaignInputs.campaignID).pipe(
              catchError(err => of({ error: err }))
            );
          }
        }
        return null;
      })
      .filter(op => op !== null);

    // Remove missing notifications
    const removeOps = prevNotifications
      .filter(n => !findByLayoutID(currNotifications, n.layoutID) && n.creationStatus !== CampaignStatus.Cancelled)
      .map(n => this.removeNotificationForExistingCampaign(locationNumber, n, campaignInputs.campaignID).pipe(
        catchError(err => of({ error: err }))
      ));

    const notificationOps = [...addOps, ...updateOps, ...removeOps];
    const notificationsChanged = notificationOps.length > 0;

    if (nameChanged || eventChanged) {
      if ('notifications' in campaignRequest) {
        delete campaignRequest.notifications;
      }
      this.httpClient.patch(
        environment.digitalMarketingCampaignsServiceURL + `/api/restaurant/campaigns/${campaignInputs.campaignID}`,
        campaignRequest, { headers: headers }
      ).subscribe({
        next: () => {
          if (notificationsChanged) {
            forkJoin(notificationOps.length ? notificationOps : [of(true)]).subscribe(results => {
              const failed = results.find(r => r && r.error);
              if (failed) {
                campaignID.error(failed.error);
                return;
              }
              campaignID.next(campaignInputs.campaignID);
              campaignID.complete();
            });
          } else {
            campaignID.next(campaignInputs.campaignID);
            campaignID.complete();
          }
        },
        error: (error) => {
          campaignID.error(error.error);
        }
      });
    } else {
      forkJoin(notificationOps.length ? notificationOps : [of(true)]).subscribe(results => {
        const failed = results.find(r => r && r.error);
        if (failed) {
          campaignID.error(failed.error);
          return;
        }
        campaignID.next(campaignInputs.campaignID);
        campaignID.complete();
      });
    }
    return campaignID.asObservable();
  }

  public addNotificationToExistingCampaign(
    locationNumber: string,
    notification: NotificationRequest,
    campaignID: string
  ): Observable<string> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    return this.httpClient.post(environment.digitalMarketingCampaignsServiceURL + `/api/restaurant/campaigns/${campaignID}/notifications`,
      notification, { headers: headers }).pipe(
      map(() => notification.layoutID)
    );
  }

  public updateNotificationForExistingCampaign(
    locationNumber: string,
    notification: Notification,
    campaignID: string
  ): Observable<string> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    return this.httpClient.patch(
      environment.digitalMarketingCampaignsServiceURL + `/api/restaurant/campaigns/${campaignID}/notifications/${notification.id}`,
      notification, { headers: headers }).pipe(
      map(() => notification.id)
    );
  }

  public removeNotificationForExistingCampaign(
    locationNumber: string,
    notification: Notification,
    campaignID: string
  ): Observable<string> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    return this.httpClient.delete(
      environment.digitalMarketingCampaignsServiceURL + `/api/restaurant/campaigns/${campaignID}/notifications/${notification.id}`, {
        headers: headers }).pipe(
      map(() => notification.id)
    );
  }

  private interpretHighMessagingResponse(apiResponse: z.infer<typeof highMessagingResponseSchema>): boolean {
    return apiResponse.customer_count > 1000;
  }

  private campaignDetailsResponseToInternalType(apiResponse: z.infer<typeof campaignDetailsSchema>): CampaignDetails {
    const notifications: Notification[] = [];
    apiResponse.notifications.forEach((notification: z.infer<typeof notificationSchema>) =>
      notifications.push({
        id: notification.id,
        creationStatus: convertToEnum(CampaignStatus, notification.creationStatus),
        sendDatetime: moment(notification.sendDatetime),
        templateID: notification.templateID,
        segment: notification.segment,
        channel: notification.channel,
        layoutID: notification.layoutID,
        emailCustomizations: {
          subject: notification.emailCustomizations.subject,
          themeID: notification.emailCustomizations.themeID,
          heroImage: {
            desktopURL: notification.emailCustomizations.heroImage.desktopURL,
            mobileURL: notification.emailCustomizations.heroImage.mobileURL,
          },
          body: notification.emailCustomizations.body,
          recruitmentUrl: notification.emailCustomizations.recruitmentUrl,
        }
      })
    );
    let reward: RewardDetails;
    if (apiResponse.reward) {
      reward = {
        failedCustomers: apiResponse.reward.failedCustomers,
        offerBatchID: apiResponse.reward.offerBatchID,
        startDatetime: moment(apiResponse.reward.startDatetime),
        endDatetime: moment(apiResponse.reward.endDatetime),
        rewardID: apiResponse.reward.rewardID,
        appMessage: apiResponse.reward.appMessage,
        includeOperatorImage: apiResponse.reward.includeOperatorImage,
        isLimitedTimeOffer: apiResponse.reward.isLimitedTimeOffer,
        includeActiveLocationRewardCustomers: apiResponse.reward.includeActiveLocationRewardCustomers,
      }
      if (apiResponse.reward.deletedAt) {
        reward['deletedAt'] = moment(apiResponse.reward.deletedAt)
      }
    }
    let event: EventDetails;
    if (apiResponse.event) {
      event = {
        venue: apiResponse.event.venue,
        startDate: moment(apiResponse.event.startDatetime),
        endDate: moment(apiResponse.event.endDatetime),
        rsvpRequired: apiResponse.event.rsvpRequired,
        limitedReservations: apiResponse.event.maxReservations !== undefined && apiResponse.event.maxReservations !== 100000,
        limitedAttendeesPerCustomer:
            apiResponse.event.maxAttendeesPerCustomer !== undefined && apiResponse.event.maxAttendeesPerCustomer !== 100000,
        adultsOnly: apiResponse.event.adultsOnly,
        maxReservations: apiResponse.event.maxReservations,
        maxAttendeesPerCustomer: apiResponse.event.maxAttendeesPerCustomer,
        startTime: moment(apiResponse.event.startDatetime).format('h:mm a'),
        endTime: moment(apiResponse.event.endDatetime).format('h:mm a'),
        remindNotRSVPed: apiResponse.notifications.some(notification => notification.layoutID === 'event-reminder-not-rsvped' &&
            notification.creationStatus !== CampaignStatus.Cancelled),
        remindRSVPed: apiResponse.notifications.some(
          notification => notification.layoutID === 'event-reminder-rsvped' &&
                notification.creationStatus !== CampaignStatus.Cancelled),
        sendCancellationNotification: apiResponse.notifications.some(notification =>
          notification.layoutID === 'event-cancellation' && notification.creationStatus !== CampaignStatus.Cancelled),
        sendUpdateNotification: apiResponse.notifications.some(notification => notification.layoutID === 'event-update'
            && notification.creationStatus !== CampaignStatus.Cancelled),
        sendReminderEmails: (apiResponse.notifications.some(
          notification => notification.layoutID === 'event-reminder-not-rsvped' &&
              notification.creationStatus !== CampaignStatus.Cancelled) ||
            apiResponse.notifications.some(
              notification => notification.layoutID === 'event-reminder-rsvped' &&
                  notification.creationStatus !== CampaignStatus.Cancelled)),
        subject: apiResponse.event.subject,
        body: apiResponse.event.body,
      }
    }
    return {
      id: apiResponse.id,
      type: convertToEnum(CampaignType, apiResponse.type),
      status: convertToEnum(CampaignStatus, apiResponse.status),
      name: apiResponse.name,
      createdBy: apiResponse.createdBy,
      createdAt: moment(apiResponse.createdAt),
      updatedBy: apiResponse.updatedBy,
      updatedAt: apiResponse.updatedAt ? moment(apiResponse.updatedAt) : undefined,
      notifications: notifications,
      recipients: apiResponse.recipients,
      reward: reward,
      event: event,
      templateID: apiResponse.templateID,
      audienceID: apiResponse.audienceID
    }
  }

  private recipientsResponseToInternalType(apiResponse: z.infer<typeof campaignRecipientsSchema>, campaignID?: string):
      CampaignRecipientStatuses {
    const hardBounced = apiResponse.emailStatuses?.hardBounced || []
    const softBounced = apiResponse.emailStatuses?.softBounced || []
    const deferred = apiResponse.emailStatuses?.deferred || []
    const failed = [...hardBounced, ...softBounced, ...deferred]
    let internalObject = {
      campaignRecipients: apiResponse.campaignRecipients,
      emailStatuses: {
        sent: new Set(apiResponse.emailStatuses?.sent),
        delivered: new Set(apiResponse.emailStatuses?.delivered),
        opened: new Set(apiResponse.emailStatuses?.opened),
        dropped: new Set(apiResponse.emailStatuses?.dropped),
        failed: new Set(failed),
        failedProcessing: new Set(apiResponse.emailStatuses?.failed),
        spam: new Set(apiResponse.emailStatuses?.spam),
        unsubscribed: new Set(apiResponse.emailStatuses?.unsubscribed),
      },

      rewardStatuses: {
        locationRewardExclusion: new Set(apiResponse.rewardStatuses?.locationRewardExclusion),
        duplicateRewardExclusion: new Set(apiResponse.rewardStatuses?.duplicateRewardExclusion),
        rewardCreationError: new Set(apiResponse.rewardStatuses?.rewardCreationError),
        loaded: new Set(apiResponse.rewardStatuses?.rewardLoaded),
        redeemedAtIssuedLocation: new Set(apiResponse.rewardStatuses?.redeemedAtIssuedLocation),
        redeemedAtOtherLocation: new Set(apiResponse.rewardStatuses?.redeemedAtOtherLocation),
      },

      eventRSVPSummary: {
        going: (apiResponse.eventRSVPSummary?.going || []).map(attendee => ({
          cfaId: attendee.cfaId,
          adultsAttending: attendee.adultsAttending,
          kidsAttending: attendee.kidsAttending,
        })),
        notGoing: new Set(apiResponse.eventRSVPSummary?.notGoing),
        maybe: new Set(apiResponse.eventRSVPSummary?.maybe),
      },
      attendeeSummary: {
        totalAttendees: apiResponse.attendeeSummary?.totalAttendees,
        totalAdults: apiResponse.attendeeSummary?.totalAdults,
        totalKids: apiResponse.attendeeSummary?.totalKids,
        maybeTotalAttendees: apiResponse.attendeeSummary?.maybeTotalAttendees,
        maybeAdults: apiResponse.attendeeSummary?.maybeAdults,
        maybeKids: apiResponse.attendeeSummary?.maybeKids,
      }
    }
    if (campaignID) {
      internalObject['campaignID'] = campaignID
    }
    return internalObject;
  }

  private getLayoutID(campaignType: CampaignType): string {
    const layoutMap: Record<CampaignType, string> = {
      [CampaignType.Event]: 'event-invitation',
      [CampaignType.Reward]: 'reward-notification',
      [CampaignType.Automation]: 'reward-notification',
      [CampaignType.Notification]: 'notification'
    };
    return layoutMap[campaignType] || 'notification';
  }

  private buildEmailCustomizations(inputs: CampaignInputs): EmailCustomizationsRequest {
    return {
      // Prefer event subject/body when present; fall back if missing/empty so
      // non-event flows (or blank event fields) keep customizations content.
      subject: inputs.eventInformation?.subject || inputs.customizations.subject,
      body: inputs.eventInformation?.body || inputs.customizations.body,
      themeID: inputs.customizations.themeID,
      heroImage: inputs.customizations.heroImage,
      recruitmentUrl: inputs.customizations.includeRecruitment ? inputs.customizations.recruitmentURL : null,
    };
  }

  private createNotificationRequest(
    layoutID: string,
    sendDatetime: string,
    emailCustomizations: EmailCustomizationsRequest,
    segment: string = this.DEFAULT_SEGMENT,
  ): NotificationRequest {
    return {
      layoutID,
      sendDatetime,
      emailCustomizations,
      segment,
      channel: this.DEFAULT_CHANNEL,
    };
  }

  private buildNotification(inputs: CampaignInputs, layoutID?: string): NotificationRequest {
    const isPastSend = moment(inputs.sendingOptions.sendDate).isSameOrBefore(moment())
      && !(inputs.eventInformation?.sendUpdateNotification || inputs.eventInformation?.sendCancellationNotification);
    // Remap past times to now+10s for send-now flows only. Scheduled sends that have
    // elapsed are rejected earlier via isScheduledSendTimeElapsed.
    let sendTime = isPastSend && inputs.sendingOptions.sendImmediate !== false
      ? moment().clone().add(10, 'seconds')
      : inputs.sendingOptions.sendDate;
    const layout = layoutID || this.getLayoutID(convertToEnum(CampaignType, inputs.template.campaignType));
    const sendDatetime = moment(sendTime).toISOString();
    const emailCustomizations = this.buildEmailCustomizations(inputs);

    return this.createNotificationRequest(layout, sendDatetime, emailCustomizations);
  }

  private isScheduledSendTimeElapsed(inputs: CampaignInputs): boolean {
    if (inputs?.sendingOptions?.sendImmediate !== false) {
      return false;
    }
    // Event update/cancel flows intentionally keep past invitation send times.
    if (inputs?.eventInformation?.sendUpdateNotification
        || inputs?.eventInformation?.sendCancellationNotification) {
      return false;
    }
    return moment(inputs.sendingOptions.sendDate).isSameOrBefore(moment());
  }

  private buildEventNotifications(inputs: CampaignInputs): NotificationRequest[] {
    const notifications: NotificationRequest[] = [this.buildNotification(inputs)];
    const emailCustomizations = this.buildEmailCustomizations(inputs);

    if (inputs.eventInformation?.remindRSVPed) {
      const reminderDate = inputs.eventInformation.startDate.clone().subtract(1, 'days');
      if (reminderDate.day() === 0) {
        reminderDate.subtract(1, 'days');
      }
      notifications.push(
        this.createNotificationRequest(
          'event-reminder-rsvped',
          reminderDate.toISOString(),
          emailCustomizations,
          this.RSVPED_YES_SEGMENT
        )
      );
    }

    if (inputs.eventInformation?.remindNotRSVPed) {
      const reminderDate = inputs.eventInformation.startDate.clone().subtract(3, 'days');
      if (reminderDate.day() === 0) {
        reminderDate.subtract(1, 'days');
      }
      notifications.push(
        this.createNotificationRequest(
          'event-reminder-not-rsvped',
          reminderDate.toISOString(),
          emailCustomizations,
          this.NOT_RSVPED_SEGMENT
        )
      );
    }

    if (inputs.eventInformation?.sendCancellationNotification) {
      const sendDatetime = moment().add(15, 'seconds').toISOString();
      notifications.push(
        this.createNotificationRequest(
          'event-cancellation',
          sendDatetime,
          emailCustomizations,
          this.RSVPED_YES_OR_PENDING_SEGMENT
        )
      );
    }

    if (inputs.eventInformation?.sendUpdateNotification) {
      const sendDatetime = moment().add(15, 'seconds').toISOString();
      notifications.push(
        this.createNotificationRequest(
          'event-update',
          sendDatetime,
          emailCustomizations,
          this.RSVPED_YES_OR_PENDING_SEGMENT
        )
      );
    }

    return notifications;
  }

  private buildBaseCampaignRequest(inputs: CampaignInputs, type: CampaignType): BaseCampaignRequest {
    return {
      type,
      templateID: inputs.template.id,
      name: inputs.customizations.name,
      title: inputs.template.title,
      audienceID: inputs.audienceID,
      ...(inputs.goal ? { goal: inputs.goal } : {}),
    };
  }

  private buildRewardCampaignRequest(inputs: CampaignInputs): CampaignRequest {
    const sendTime = inputs.rewardInformation.startDatetime.isSameOrBefore(moment()) ?
      inputs.rewardInformation.startDatetime.clone().add(10, 'seconds') : inputs.rewardInformation.startDatetime;
    return {
      ...this.buildBaseCampaignRequest(inputs, CampaignType.Reward),
      notifications: [this.buildNotification(inputs)],
      reward: {
        startDatetime: sendTime.toISOString(),
        endDatetime: inputs.rewardInformation.endDatetime.toISOString(),
        rewardId: inputs.rewardInformation.rewardID,
        appMessage: inputs.rewardInformation.appMessage,
        includeOperatorImage: inputs.rewardInformation.includeOperatorImage,
        includeActiveLocationRewardCustomers: inputs.rewardInformation.includeActiveLocationRewardCustomers,
      },
    };
  }

  private buildEventCampaignRequest(inputs: CampaignInputs): CampaignRequest {
    return {
      ...this.buildBaseCampaignRequest(inputs, CampaignType.Event),
      notifications: this.buildEventNotifications(inputs),
      event: {
        startDatetime: inputs.eventInformation.startDate.toISOString(),
        endDatetime: inputs.eventInformation.endDate.toISOString(),
        venue: inputs.eventInformation.venue,
        rsvpRequired: inputs.eventInformation.rsvpRequired,
        adultsOnly: inputs.eventInformation.adultsOnly,
        maxReservations: inputs.eventInformation.limitedReservations === true ? inputs.eventInformation.maxReservations : 100000,
        maxAttendeesPerCustomer:
            inputs.eventInformation.limitedAttendeesPerCustomer === true ? inputs.eventInformation.maxAttendeesPerCustomer : 100000,
        subject: inputs.eventInformation.subject,
        body: inputs.eventInformation.body,
      },
    };
  }

  private buildNotificationCampaignRequest(inputs: CampaignInputs): CampaignRequest {
    return {
      ...this.buildBaseCampaignRequest(inputs, CampaignType.Notification),
      notifications: [this.buildNotification(inputs)],
    };
  }

  private formatCampaignRequest(inputs: CampaignInputs): CampaignRequest {
    switch (inputs.template.campaignType) {
      case CampaignType.Reward:
        return this.buildRewardCampaignRequest(inputs);
      case CampaignType.Event:
        return this.buildEventCampaignRequest(inputs);
      case CampaignType.Notification:
        return this.buildNotificationCampaignRequest(inputs);
      default:
        throw new Error(`Unsupported campaign type: ${inputs.template.campaignType}`);
    }
  }

}

export const campaignRecipientsSchema = z.object({
  campaignRecipients: z.array(z.string()).optional(),
  emailStatuses: z.object({
    attempted: z.array(z.string()).optional(),
    sent: z.array(z.string()).optional(),
    delivered: z.array(z.string()).optional(),
    deferred: z.array(z.string()).optional(),
    dropped: z.array(z.string()).optional(),
    hardBounced: z.array(z.string()).optional(),
    softBounced: z.array(z.string()).optional(),
    failed: z.array(z.string()).optional(),
    opened: z.array(z.string()).optional(),
    clicked: z.array(z.string()).optional(),
    spam: z.array(z.string()).optional(),
    unsubscribed: z.array(z.string()).optional(),
  }).optional(),
  rewardStatuses: z.object({
    locationRewardExclusion: z.array(z.string()).optional(),
    duplicateRewardExclusion: z.array(z.string()).optional(),
    rewardCreationError: z.array(z.string()).optional(),
    rewardLoaded: z.array(z.string()).optional(),
    redeemedAtIssuedLocation: z.array(z.string()).optional(),
    redeemedAtOtherLocation: z.array(z.string()).optional(),
  }).optional(),
  eventRSVPSummary: z.object({
    going: z.array(z.object({
      cfaId: z.string(),
      adultsAttending: z.number(),
      kidsAttending: z.number(),
    })).optional(),
    notGoing: z.array(z.string()).optional(),
    maybe: z.array(z.string()).optional(),
  }).optional(),
  attendeeSummary: z.object({
    totalAttendees: z.number().optional(),
    totalAdults: z.number().optional(),
    totalKids: z.number().optional(),
    maybeTotalAttendees: z.number().optional(),
    maybeAdults: z.number().optional(),
    maybeKids: z.number().optional(),
  }).optional(),
})

export const notificationSchema = z.object({
  id: z.string(),
  creationStatus: z.string(),
  sendDatetime: z.string(),
  templateID: z.number().optional(),
  segment: z.string(),
  channel: z.string(),
  layoutID: z.string(),
  emailCustomizations: z.object({
    themeID: z.string(),
    heroImage: z.object({
      desktopURL: z.string(),
      mobileURL: z.string()
    }).optional(),
    subject: z.string(),
    body: z.string(),
    recruitmentUrl: z.string().optional(),
  }),
})

export const highMessagingResponseSchema = z.object({
  customer_count: z.number(),
})

export const campaignDetailsSchema = z.object({
  id: z.string(),
  type: z.string(),
  name: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedBy: z.string().optional(),
  updatedAt: z.string().optional(),
  notifications: z.array(notificationSchema),
  event: z.object({
    startDatetime: z.string(),
    endDatetime: z.string(),
    venue: z.string(),
    rsvpRequired: z.boolean(),
    adultsOnly: z.boolean(),
    maxReservations: z.number().optional(),
    maxAttendeesPerCustomer: z.number().optional(),
    subject: z.string().optional(),
    body: z.string().optional(),
  }).optional(),
  reward: z.object({
    failedCustomers: z.string().optional(),
    offerBatchID: z.string().optional(),
    startDatetime: z.string(),
    endDatetime: z.string(),
    appMessage: z.string(),
    includeOperatorImage: z.boolean(),
    includeActiveLocationRewardCustomers: z.boolean(),
    isLimitedTimeOffer: z.boolean().optional(),
    rewardID: z.string(),
    deletedAt: z.string().optional()
  }).optional(),
  status: z.string(),
  recipients: z.string(),
  templateID: z.string(),
  audienceID: z.string().optional()
});

export const createCampaignResponseSchema = z.object({
  id: z.string(),
})

function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }
  return true;
}
